import { useMemo } from 'react';

import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

import { useMultiValueInput } from './hooks/useMultiValueInput';
import { Tag } from './Tag';

interface TextValueInputProps {
  value: string | string[];
  onChange: (newValue: string | string[]) => void;
  disabled: boolean;
  filterDefinition: SimpleFilterDefinition;
  allowMultiple?: boolean;
}

export const TextValueInput = ({
  value,
  onChange,
  disabled,
  filterDefinition,
  allowMultiple = false,
}: TextValueInputProps) => {
  // Validation function for text
  const validate = useMemo(
    () => (val: string) => {
      const { required, pattern } = filterDefinition.validation || {};

      if (required && !val) {
        return {
          valid: false,
          error: `${filterDefinition.uiField} is required`,
        };
      }

      if (pattern && !pattern.test(val)) {
        return {
          valid: false,
          error: `${filterDefinition.uiField} format is invalid`,
        };
      }

      return { valid: true };
    },
    [filterDefinition]
  );

  const multiValueState = useMultiValueInput({
    value,
    onChange,
    validate,
    disabled,
  });

  // Single value mode
  if (!allowMultiple) {
    const formatValue = (val: string | string[]): string => {
      if (Array.isArray(val)) {
        return val.join(', ');
      }
      return String(val);
    };

    return (
      <input
        type="text"
        disabled={disabled}
        value={formatValue(value)}
        onChange={e => onChange(e.target.value)}
        placeholder={`Enter ${filterDefinition.uiField}...`}
        className={`flex-[3.5] rounded border px-3 py-1.5 text-sm ${
          disabled
            ? 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-secondary cursor-not-allowed opacity-90'
            : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:border-chariot-brand-primary'
        }`}
        aria-label="Filter value"
      />
    );
  }

  // Multi-value tag mode
  return (
    <div
      // eslint-disable-next-line react-hooks/refs
      ref={multiValueState.containerRef}
      className={`flex flex-[3.5] flex-wrap items-center gap-1 rounded border px-3 py-1.5 text-sm ${
        disabled
          ? 'border-chariot-stroke bg-chariot-background-primary cursor-not-allowed opacity-90'
          : 'border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary'
      }`}
      aria-label="Filter value"
    >
      {/* eslint-disable-next-line react-hooks/refs */}
      {multiValueState.tags.map((tag, index) => (
        <Tag
          key={index}
          value={tag}
          onRemove={() => multiValueState.handleRemoveTag(index)}
          disabled={disabled}
        />
      ))}
      {!disabled && (
        <input
          type="text"
          // eslint-disable-next-line react-hooks/refs
          value={multiValueState.inputValue}
          onChange={e => multiValueState.setInputValue(e.target.value)}
          // eslint-disable-next-line react-hooks/refs
          onKeyDown={multiValueState.handleKeyDown}
          placeholder={`Enter ${filterDefinition.uiField}...`}
          className="text-chariot-text-primary min-w-[120px] flex-1 border-0 bg-transparent outline-hidden"
          aria-label="Add filter value"
        />
      )}
    </div>
  );
};
